import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useCRMAuth } from './CRMAuthContext';

const CustomerContext = createContext();

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const { user: authUser } = useAuth();
  const { user: crmUser, makeAuthenticatedRequest, isAuthenticated: crmAuthenticated } = useCRMAuth();

  // Resolve user from whichever auth system is active
  const user = crmUser || authUser;

  const [suppliers, setSuppliers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ------------------------------------------------------------------
     Local suppliers (intentional client-side persistence)
  ------------------------------------------------------------------ */

  useEffect(() => {
    const email = user?.email;
    if (!email) return;

    const savedSuppliers = localStorage.getItem(`suppliers_${email}`);
    if (!savedSuppliers) return;

    try {
      setSuppliers(JSON.parse(savedSuppliers));
    } catch (err) {
      console.error('Error parsing saved suppliers:', err);
      setSuppliers([]);
    }
  }, [user?.email]);

  /* ------------------------------------------------------------------
     Marketplace showrooms — populate supplier list for customers
  ------------------------------------------------------------------ */

  useEffect(() => {
    if (!makeAuthenticatedRequest || !crmAuthenticated) return;
    fetchMarketplaceShowrooms();
  }, [makeAuthenticatedRequest, crmAuthenticated]);

  const fetchMarketplaceShowrooms = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/v1/marketplace/showrooms');
      if (!response?.ok) return;
      const data = await response.json();
      const showrooms = (data.showrooms || []).map((s) => ({
        id: s.business_id || s.id,
        name: s.name,
        email: s.contact_options?.email || '',
        phone: s.contact_options?.phone || '',
        category: s.category || 'Business',
        description: s.description || '',
        status: 'active',
        isFromMarketplace: true,
      })).filter((s) => s.id && s.name);

      if (showrooms.length > 0) {
        setSuppliers((prev) => {
          const existingIds = new Set(prev.map((s) => s.id));
          const newOnes = showrooms.filter((s) => !existingIds.has(s.id));
          return [...prev, ...newOnes];
        });
      }
    } catch (err) {
      console.error('Error fetching marketplace showrooms:', err);
    }
  };

  /* ------------------------------------------------------------------
     Conversations (backend-driven)
  ------------------------------------------------------------------ */

  useEffect(() => {
    const customerId = user?.customer_id || user?.id;
    if (!customerId) return;
    fetchConversations();
    const poll = setInterval(fetchConversations, 15000);
    return () => clearInterval(poll);
  }, [user?.customer_id, user?.id]);

  const fetchConversations = async () => {
    const customerId = user?.customer_id || user?.id;
    if (!customerId || !makeAuthenticatedRequest) return;

    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        `/api/v1/conversations/customer/${customerId}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch conversations: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------
     Supplier helpers (local-only)
  ------------------------------------------------------------------ */

  const addSupplier = (supplierData) => {
    const newSupplier = {
      id: Date.now().toString(),
      name: supplierData.name,
      email: supplierData.email,
      phone: supplierData.phone,
      category: supplierData.category,
      description: supplierData.description || '',
      website: supplierData.website || '',
      address: supplierData.address || '',
      status: 'active',
      addedDate: new Date().toISOString(),
      tickets: 0,
      rating: 0,
    };

    const updated = [...suppliers, newSupplier];
    setSuppliers(updated);

    if (user?.email) {
      localStorage.setItem(`suppliers_${user.email}`, JSON.stringify(updated));
    }

    return newSupplier;
  };

  const removeSupplier = (supplierId) => {
    const updated = suppliers.filter((s) => s.id !== supplierId);
    setSuppliers(updated);

    if (user?.email) {
      localStorage.setItem(`suppliers_${user.email}`, JSON.stringify(updated));
    }
  };

  const updateSupplier = (supplierId, updates) => {
    const updated = suppliers.map((s) =>
      s.id === supplierId ? { ...s, ...updates } : s
    );
    setSuppliers(updated);

    if (user?.email) {
      localStorage.setItem(`suppliers_${user.email}`, JSON.stringify(updated));
    }
  };

  const getSupplierById = (supplierId) =>
    suppliers.find((s) => s.id === supplierId);

  /* ------------------------------------------------------------------
     Conversations
  ------------------------------------------------------------------ */

  const createConversation = async (conversationData) => {
    const customerId = user?.customer_id || user?.id;
    if (!customerId || !makeAuthenticatedRequest) {
      throw new Error('Customer not authenticated');
    }

    try {
      const response = await makeAuthenticatedRequest('/api/v1/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer_id: customerId,
          business_id: conversationData.business_id,
          business_name: conversationData.business_name,
          category: conversationData.category,
          initial_message: conversationData.initial_message,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to create conversation: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      await fetchConversations();
      return result;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  };

  const addMessageToConversation = async (conversationId, messageData) => {
    try {
      const customerId = user?.customer_id || user?.id || user?.uid;
      const response = await makeAuthenticatedRequest(
        '/api/v1/conversations/message',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            conversation_id: conversationId,
            sender_id: messageData.sender_id || customerId,
            sender_type: messageData.sender_type || 'customer',
            message: messageData.message || messageData.text || '',
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to add message: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      await fetchConversations();
      return result;
    } catch (error) {
      console.error('Error adding message to conversation:', error);
      throw error;
    }
  };

  /* ------------------------------------------------------------------
     Provider
  ------------------------------------------------------------------ */

  const value = {
    suppliers,
    conversations,
    loading,
    addSupplier,
    removeSupplier,
    updateSupplier,
    getSupplierById,
    createConversation,
    addMessageToConversation,
    fetchConversations,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};